import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SidebarLayout from '../../components/SidebarLayout';
import ViewDataContent from '../../components/ViewDataContent';
import AddDataContent from '../../components/AddDataContent';
import './styles.css';
import { API_BASE_URL as API_URL } from '../../config/apiBase';


const AdminTablesPage = () => {
  const navigate = useNavigate();
  const { id: entityId } = useParams(); // entity_id from URL
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [selectedSchemaId, setSelectedSchemaId] = useState(null);
  const [addingDataSchemaId, setAddingDataSchemaId] = useState(null);
  const [includeDeleted, setIncludeDeleted] = useState(false);

  useEffect(() => {
    if (entityId) {
      fetchSchemas();
      fetchSchoolInfo();
    }
  }, [entityId, includeDeleted]);

  const fetchSchemas = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = `${API_URL}/admin/entities/${entityId}/schemas${includeDeleted ? '?include_deleted=true' : ''}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch schemas');
      }

      const data = await response.json();
      setSchemas(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchoolInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/entities/${entityId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch school information');
      }

      const data = await response.json();
      setSchoolInfo(data.data.school);
    } catch (err) {
      console.error('Failed to fetch school info:', err.message);
    }
  };

  const handleAddData = (schemaId) => {
    // Toggle: if clicking the same schema, close it; otherwise, open the clicked one
    if (addingDataSchemaId === schemaId) {
      setAddingDataSchemaId(null);
    } else {
      setAddingDataSchemaId(schemaId);
      // Close view data if it's open for the same schema
      if (selectedSchemaId === schemaId) {
        setSelectedSchemaId(null);
      }
    }
  };

  const handleAddDataSuccess = () => {
    // Refresh the view data if it's open
    if (selectedSchemaId) {
      // Trigger a re-render by toggling
      const currentId = selectedSchemaId;
      setSelectedSchemaId(null);
      setTimeout(() => {
        setSelectedSchemaId(currentId);
      }, 100);
    }
  };

  const handleViewData = (schemaId) => {
    // Toggle: if clicking the same schema, close it; otherwise, open the clicked one
    if (selectedSchemaId === schemaId) {
      setSelectedSchemaId(null);
    } else {
      setSelectedSchemaId(schemaId);
    }
  };

  const handleEditTable = (schemaId) => {
    navigate(`/admin/entities/${entityId}/edit-table/${schemaId}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getColumnCount = (schemaDefinition) => {
    try {
      const definition = typeof schemaDefinition === 'string' 
        ? JSON.parse(schemaDefinition) 
        : schemaDefinition;
      return definition.columns ? definition.columns.length : 0;
    } catch {
      return 0;
    }
  };

  if (loading) {
    return (
      <div className="tables-container">
        <div className="tables-loading">
          <div className="loading-spinner"></div>
          <p>Loading tables...</p>
        </div>
      </div>
    );
  }

  return (
    <SidebarLayout>
      <div className="tables-container">
      <div className="tables-header">
        <div>
          <h1>Data Tables</h1>
          <p>Manage school's data tables and records</p>
          {schoolInfo && (
            <div className="tables-school-info">
              <span className="tables-school-label">School:</span>
              <span className="tables-school-name">{schoolInfo.school_name}</span>
            </div>
          )}
        </div>
        <div className="tables-header-actions">
          <button 
            onClick={() => navigate(`/admin/entities/${entityId}/create-table`)}
            className="create-table-btn"
          >
            + Create New Table
          </button>
          <button 
            onClick={() => navigate(`/admin/entities/${entityId}`)}
            className="tables-back-btn"
          >
            Back to Entity Details
          </button>
          <label className="checkbox-label" style={{ marginLeft: 12 }}>
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={() => setIncludeDeleted(v => !v)}
              className="checkbox-input"
            />
            <span className="checkbox-text">Show deleted tables</span>
          </label>
        </div>
      </div>

      {error && (
        <div className="tables-error">
          {error}
        </div>
      )}

      <div className="tables-content">
        {schemas.length === 0 ? (
          <div className="tables-empty-state">
            <div className="empty-state-icon">📊</div>
            <h3>No Tables Created Yet</h3>
            <p>Create the first data table to start organizing the school's information.</p>
            <button 
              onClick={() => navigate(`/admin/entities/${entityId}/create-table`)}
              className="create-first-table-btn"
            >
              Create First Table
            </button>
          </div>
        ) : (
          <div className="tables-grid">
            {schemas.map((schema) => (
              <React.Fragment key={schema.schema_id}>
                <div className="table-card">
                  <div className="table-card-header">
                    <h3 className="table-name">{schema.table_name}</h3>
                    <div className="table-meta">
                      <span className="table-columns">
                        {getColumnCount(schema.schema_definition)} columns
                      </span>
                      <span className="table-version">v{schema.version}</span>
                    </div>
                  </div>
                  
                  <div className="table-card-body">
                    <div className="table-info">
                      <div className="table-info-item">
                        <span className="table-info-label">Created:</span>
                        <span className="table-info-value">
                          {formatDate(schema.created_at)}
                        </span>
                      </div>
                      <div className="table-info-item">
                        <span className="table-info-label">Schema ID:</span>
                        <span className="table-info-value">{schema.schema_id}</span>
                      </div>
                    </div>
                  </div>

                  <div className="table-card-actions">
                    <button
                      onClick={() => handleAddData(schema.schema_id)}
                      className={`action-btn add-data-btn ${addingDataSchemaId === schema.schema_id ? 'active' : ''}`}
                    >
                      {addingDataSchemaId === schema.schema_id ? 'Hide Form' : 'Add Data'}
                    </button>
                    <button
                      onClick={() => handleViewData(schema.schema_id)}
                      className={`action-btn view-data-btn ${selectedSchemaId === schema.schema_id ? 'active' : ''}`}
                    >
                      {selectedSchemaId === schema.schema_id ? 'Hide Data' : 'View Data'}
                    </button>
                    <button
                      onClick={() => handleEditTable(schema.schema_id)}
                      className="action-btn edit-table-btn"
                    >
                      Edit Table
                    </button>
                  </div>
                </div>
                
                {addingDataSchemaId === schema.schema_id && (
                  <div className="inline-add-data-wrapper">
                    <AddDataContent 
                      schemaId={schema.schema_id} 
                      isInline={true}
                      adminMode={true}
                      entityId={entityId}
                      onBack={() => setAddingDataSchemaId(null)}
                      onSuccess={handleAddDataSuccess}
                    />
                  </div>
                )}
                
                {selectedSchemaId === schema.schema_id && (
                  <div className="inline-view-data-wrapper">
                    <ViewDataContent 
                      schemaId={schema.schema_id} 
                      isInline={true}
                      adminMode={true}
                      entityId={entityId}
                      includeDeleted={includeDeleted}
                      onToggleIncludeDeleted={() => setIncludeDeleted(v => !v)}
                      onAddData={(id) => {
                        setSelectedSchemaId(null);
                        setAddingDataSchemaId(id);
                      }}
                      onBack={() => setSelectedSchemaId(null)}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
      </div>
    </SidebarLayout>
  );
};

export default AdminTablesPage;

